import React, { useState, useEffect, useMemo } from 'react';
import {
  FiX, FiArrowLeft, FiFileText, FiUsers, FiTool, FiArchive, FiBell,
  FiHash, FiSettings, FiGrid, FiInbox, FiFile, FiRefreshCw, FiCalendar,
  FiList, FiTruck, FiClock, FiDollarSign, FiSearch, FiPackage, FiBarChart2,
  FiBookOpen, FiColumns, FiUserPlus, FiGlobe, FiClipboard, FiPhone,
  FiCpu, FiAlertCircle, FiAward, FiMapPin, FiShoppingCart, FiRss,
  FiMessageSquare, FiMail, FiCloud, FiEdit3, FiLayers, FiZap, FiTrendingUp,
  FiPieChart, FiMonitor, FiLink, FiShield, FiTerminal, FiDatabase, FiSave,
  FiSliders, FiUser, FiBox,
} from 'react-icons/fi';

const CENTER = 300;
const OUTER_R = 290;
const INNER_R = 78;
const LABEL_R = 190;

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const arcPath = (startAngle, endAngle) => {
  const start = polarToCartesian(CENTER, CENTER, OUTER_R, endAngle);
  const end = polarToCartesian(CENTER, CENTER, OUTER_R, startAngle);
  const innerStart = polarToCartesian(CENTER, CENTER, INNER_R, startAngle);
  const innerEnd = polarToCartesian(CENTER, CENTER, INNER_R, endAngle);
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1';
  return [
    'M', start.x, start.y,
    'A', OUTER_R, OUTER_R, 0, largeArc, 0, end.x, end.y,
    'L', innerStart.x, innerStart.y,
    'A', INNER_R, INNER_R, 0, largeArc, 1, innerEnd.x, innerEnd.y,
    'Z',
  ].join(' ');
};

const CATEGORY_ICONS = {
  'Ordre & Produksjon': FiFileText,
  'Team & Ansatte': FiUsers,
  'Maskiner': FiTool,
  'Lager & Sertifikater': FiArchive,
  'Kommunikasjon': FiBell,
  'Verktøy': FiHash,
  'Administrasjon': FiSettings,
  'Annet': FiGrid,
};

const CATEGORY_COLORS = {
  'Ordre & Produksjon': '#3E6FA3',
  'Team & Ansatte': '#1E8A5F',
  'Maskiner': '#E8622C',
  'Lager & Sertifikater': '#B7791F',
  'Kommunikasjon': '#E8622C',
  'Verktøy': '#3E6FA3',
  'Administrasjon': '#C63D3D',
  'Annet': '#475569',
};

const WIDGET_ICONS = {
  'foresporsler': FiInbox, 'workOrders': FiFileText, 'workOrdersList': FiFile,
  'ordersFlow': FiRefreshCw, 'productionPlan': FiCalendar, 'jobQueue': FiList,
  'deliveryDisplay': FiTruck, 'timeline': FiClock, 'bom': FiDollarSign,
  'unimicrLookup': FiSearch, 'partsExplorer': FiGrid, 'incomingDelivery': FiPackage,
  'deliveryTracking': FiPackage, 'dailyReport': FiClipboard,
  'teamMembers': FiUsers, 'teamCapacity': FiBarChart2, 'employeeCalendar': FiBookOpen,
  'anstattkalendar': FiColumns, 'teams': FiUserPlus, 'alleAnsatte': FiGlobe,
  'operatorWork': FiClipboard, 'phoneDirectory': FiPhone, 'absenceRequest': FiClock,
  'machines': FiTool, 'machineMonitor': FiCpu, 'alarmLog': FiAlertCircle,
  'steelCertificates': FiAward, 'steelInventory': FiArchive,
  'storageLocations': FiMapPin, 'forbruksskap': FiBox, 'steelPurchases': FiShoppingCart,
  'machiningTools': FiTool,
  'varslinger': FiBell, 'feed': FiRss, 'aiChat': FiMessageSquare, 'email': FiMail,
  'weather': FiCloud, 'vektKalkulator': FiHash, 'pdfRedact': FiEdit3,
  'cadViewer': FiLayers, 'smartScheduler': FiZap, 'kpiDashboard': FiTrendingUp,
  'statistics': FiPieChart,
  'adminPanel': FiMonitor, 'configuration': FiSettings, 'quickLinks': FiLink,
  'roles': FiShield, 'auditLog': FiTerminal, 'unimicro': FiDatabase,
  'systemBackup': FiSave, 'brukere': FiUser,
};

const getIconForWidget = (id) => WIDGET_ICONS[id] || FiBox;

const RadialSpeedDial = ({ categories, onClose, onWidgetSelect }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const currentItems = useMemo(() => {
    if (activeCategory) {
      const cat = categories.find(c => c.id === activeCategory);
      return cat ? cat.items : [];
    }
    return categories;
  }, [activeCategory, categories]);

  const sliceAngle = 360 / (currentItems.length || 1);

  const handleSliceClick = (item) => {
    if (activeCategory) {
      onWidgetSelect(item.id);
      onClose();
    } else {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveCategory(item.id);
        setIsTransitioning(false);
      }, 180);
    }
  };

  const handleBack = () => {
    if (activeCategory) {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveCategory(null);
        setIsTransitioning(false);
      }, 180);
    } else {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'relative',
          width: '900px', height: '900px',
          maxWidth: '95vw', maxHeight: '95vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <svg
          viewBox="0 0 600 600"
          style={{
            width: '100%', height: '100%',
            filter: 'drop-shadow(0 8px 30px rgba(0,0,0,0.3))',
            opacity: isTransitioning ? 0 : 1,
            transition: 'opacity 180ms ease-in-out',
          }}
        >
          {/* Slices */}
          {currentItems.map((item, i) => {
            const startAngle = i * sliceAngle;
            const endAngle = (i + 1) * sliceAngle;
            const midAngle = (startAngle + endAngle) / 2;
            const path = arcPath(startAngle, endAngle);
            const labelPos = polarToCartesian(CENTER, CENTER, LABEL_R, midAngle);
            const color = activeCategory
              ? (item.color || CATEGORY_COLORS[item.category] || '#475569')
              : (item.color || CATEGORY_COLORS[item.label] || '#475569');
            const Icon = activeCategory
              ? getIconForWidget(item.id)
              : (CATEGORY_ICONS[item.label] || FiGrid);

            return (
              <g
                key={item.id}
                onClick={() => handleSliceClick(item)}
                style={{ cursor: 'pointer' }}
              >
                <path
                  d={path}
                  fill={color}
                  fillOpacity={0.85}
                  stroke="white"
                  strokeWidth={2}
                  style={{ transition: 'fill-opacity 150ms' }}
                  onMouseEnter={(e) => e.target.style.fillOpacity = 1}
                  onMouseLeave={(e) => e.target.style.fillOpacity = 0.85}
                />
                <foreignObject
                  x={labelPos.x - 65}
                  y={labelPos.y - 42}
                  width={130}
                  height={84}
                  style={{ pointerEvents: 'none', overflow: 'visible' }}
                >
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: 'white', textAlign: 'center',
                  }}>
                    <Icon size={42} style={{ marginBottom: 4 }} />
                    <span style={{
                      fontSize: '16px', fontWeight: 600,
                      textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                      lineHeight: 1.15, maxWidth: '125px',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {activeCategory ? item.label : item.label}
                    </span>
                  </div>
                </foreignObject>
              </g>
            );
          })}

          {/* Center button */}
          <g onClick={handleBack} style={{ cursor: 'pointer' }}>
            <circle
              cx={CENTER} cy={CENTER} r={INNER_R - 4}
              fill="white"
              stroke="#e2e8f0"
              strokeWidth={2}
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }}
            />
            <foreignObject
              x={CENTER - 36} y={CENTER - 36}
              width={72} height={72}
              style={{ pointerEvents: 'none' }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '100%', height: '100%',
              }}>
                {activeCategory
                  ? <FiArrowLeft size={42} color="#475569" />
                  : <FiX size={42} color="#ef4444" />
                }
              </div>
            </foreignObject>
          </g>
        </svg>

        {/* Level indicator */}
        <div style={{
          position: 'absolute', bottom: '-30px', left: 0, right: 0,
          textAlign: 'center', color: 'white', fontSize: '0.9rem',
          textShadow: '0 1px 4px rgba(0,0,0,0.5)',
        }}>
          {activeCategory
            ? `${categories.find(c => c.id === activeCategory)?.label || ''} — klikk en modul for å åpne`
            : 'Dobbeltklikk bakgrunn for å lukke · Velg en kategori'
          }
        </div>
      </div>
    </div>
  );
};

export default RadialSpeedDial;
